import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { RxUtils, Tr } from '@utils';
import { AdditionalService, additionalServiceService, PaymentRecurrence, PaymentType } from '@services';
import { Grid, Stack } from '@mui/material';
import { FormikNumber, FormikRadio, FormikTextfield, ImageUpload } from '@components/common';
import { FormStepGreySection, FormStepLayout, FormStepQuestion } from '@components/portal/common';

type FormFields = Extendable<{
	name: string;
	summary?: string;
	description?: string;
	price?: number;
	serviceType?: string;
	supplier?: string;
	paymentRecurrence?: string;
	paymentType?: string;
}>;

export interface IAddEditServiceFormProps {
	values?: Partial<AdditionalService>;
	onSubmit?: (values: Partial<AdditionalService>, newLogo?: File) => void;
}

export const AddEditServiceForm: FC<IAddEditServiceFormProps> = ({ values, onSubmit }) => {
	const { t } = useTranslation('portal');
	const [imagesChanged, setImagesChanged] = useState(false);
	const [currentLogo, setCurrentLogo] = useState<string[]>([]);
	const [newLogo, setNewLogo] = useState<File>();

	useEffect(() => {
		values?.id &&
			RxUtils.promisify(additionalServiceService.getLogo(values as AdditionalService), (logo) => setCurrentLogo(logo ? [logo] : []));
	}, [values]);

	const formik = useFormik<FormFields>({
		initialValues: {
			name: values?.name || '',
			summary: values?.summary || '',
			description: values?.description || '',
			price: values?.price || '',
			serviceType: values?.serviceType || '',
			supplier: values?.supplier || '',
			paymentRecurrence: values?.paymentRecurrence || '',
			paymentType: values?.paymentType || ''
		},
		enableReinitialize: true,
		validateOnChange: true,
		validateOnMount: true,
		validationSchema: yup.object({
			name: yup.string().required(t('field-required')),
			summary: yup.string().required(t('field-required')),
			description: yup.string().required(t('field-required')),
			price: yup.number().required(t('field-required')),
			serviceType: yup.string().required(t('field-required')),
			supplier: yup.string().required(t('field-required')),
			paymentRecurrence: yup.string().required(t('field-required')),
			paymentType: yup.string().required(t('field-required'))
		}),
		onSubmit: ({ name, summary, description, serviceType, supplier, price, paymentRecurrence, paymentType }, api) => {
			onSubmit?.(
				{
					name,
					summary,
					description,
					price,
					serviceType,
					supplier,
					paymentRecurrence,
					paymentType
				},
				newLogo
			);
			api.setSubmitting(false);
		}
	});
	const { submitForm, handleSubmit } = formik;
	const disabled = imagesChanged ? false : !formik.isValid || formik.isSubmitting;

	return (
		<form onSubmit={handleSubmit} style={{ flexGrow: 1, overflowY: 'hidden' }}>
			<FormStepLayout
				primaryButtonProps={{
					disabled,
					children: (
						<Tr.Portal
							path={values ? 'package-deal.add-edit-service.update-service' : 'package-deal.add-edit-service.add-service'}
						/>
					),
					onClick: submitForm,
					'data-testid': 'add-edit-service-next-button'
				}}
				hideSecondaryButton
			>
				<Stack direction="column" spacing={8}>
					<FormStepQuestion
						title={<Tr.Portal path="package-deal.add-edit-service.service-details" />}
						titleProps={{ variant: 'h4' }}
					>
						<Grid container rowSpacing={5} columnSpacing={4}>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="name"
									title={t('package-deal.add-edit-service.service-name')}
									placeholder={t('package-deal.add-edit-service.service-name-placeholder')}
									required
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="summary"
									title={t('package-deal.add-edit-service.service-summary')}
									placeholder={t('package-deal.add-edit-service.service-summary-placeholder')}
									required
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="description"
									title={t('package-deal.add-edit-service.service-description')}
									placeholder={t('package-deal.add-edit-service.service-description-placeholder')}
									required
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="price"
									title={t('package-deal.add-edit-service.price')}
									placeholder={t('package-deal.add-edit-service.price-placeholder')}
									required
									decimal={2}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="serviceType"
									title={t('package-deal.add-edit-service.service-type')}
									placeholder={t('package-deal.add-edit-service.service-type-placeholder')}
									required
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="supplier"
									title={t('package-deal.add-edit-service.supplier')}
									placeholder={t('package-deal.add-edit-service.supplier-placeholder')}
									required
								/>
							</Grid>
							<Grid item xs={4}>
								<ImageUpload
									title={<Tr.Portal path="package-deal.logo" />}
									images={currentLogo}
									onChange={(files) => (setNewLogo(files?.[0]), setImagesChanged(true))}
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>
					<Stack direction="column" spacing={2}>
						<FormStepQuestion
							title={<Tr.Portal path="package-deal.add-edit-service.payment-details" />}
							titleProps={{ variant: 'h4' }}
						>
							<Grid container rowSpacing={5} columnSpacing={4}>
								<Grid item xs={4}>
									<FormikRadio
										formikApi={formik}
										name="paymentRecurrence"
										value={PaymentRecurrence.ONE_TIME}
										formControlLabelProps={{
											label: <Tr.Portal path="package-deal.add-edit-service.recurrence-one-time" />
										}}
									/>
								</Grid>
								<Grid item xs={4}>
									<FormikRadio
										formikApi={formik}
										name="paymentRecurrence"
										value={PaymentRecurrence.RECURRING}
										formControlLabelProps={{
											label: <Tr.Portal path="package-deal.add-edit-service.recurrence-recurring" />
										}}
									/>
								</Grid>
							</Grid>
						</FormStepQuestion>
						<FormStepGreySection sx={{ marginX: `-24px !important` }}>
							<Grid container rowSpacing={5} columnSpacing={4}>
								<Grid item xs={4}>
									<FormikRadio
										formikApi={formik}
										name="paymentType"
										value={PaymentType.PER_VEHICLE}
										formControlLabelProps={{
											label: <Tr.Portal path="package-deal.add-edit-service.payment-type-per-vehicle" />
										}}
									/>
								</Grid>
								<Grid item xs={4}>
									<FormikRadio
										formikApi={formik}
										name="paymentType"
										value={PaymentType.PER_FLEET}
										formControlLabelProps={{
											label: <Tr.Portal path="package-deal.add-edit-service.payment-type-per-fleet" />
										}}
									/>
								</Grid>
								<Grid item xs={4}>
									<FormikRadio
										formikApi={formik}
										name="paymentType"
										value={PaymentType.PER_DRIVER}
										formControlLabelProps={{
											label: <Tr.Portal path="package-deal.add-edit-service.payment-type-per-driver" />
										}}
									/>
								</Grid>
							</Grid>
						</FormStepGreySection>
					</Stack>
				</Stack>
			</FormStepLayout>
		</form>
	);
};
